import {
  FileDownloadOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import { isNil } from "lodash";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PrimaryContentCard from "../../../../../_src_shared/components/layout/PrimaryContentCard";
import Flx from "../../../../../components/layout/Flx";

import { useFormState } from "react-final-form";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import { formatAddressObjectFullAddress } from "../../../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { formatBorrowerObjectFullName } from "../../../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import RffForm from "../../../../../components/finalForm/RffForm";
import RffFieldsRenderer from "../../../../../components/finalForm/shared/RffFieldsRenderer";
import RffGroup from "../../../../../components/finalForm/shared/RffGroup";
import PdfPreviewer from "../../../../../components/pdf/components/PdfPreviewer";
import LoanTermsSheetPDF from "../../../../../components/pdf/LoanTermsSheetPDF/LoanTermsSheetPDF";
import Txt from "../../../../../components/typography/Txt";
import { usePdfDownloadHook } from "../../../../../hooks/usePdfDownloadHook";
import LoanTermsSheetHhbPDF from "../../../../../components/pdf/LoanTermsSheetPDF/LoanTermsSheetHhbPDF";

const mapLoanDataToLetterOfIntent = ({ loanData, loanTerms }) => {
  return {
    // ##### For displaying of the Properties #####
    // Currently for property address, the address is being displayed at the top of the PDF
    // - If there are multiple properties how do we want to display? Do we want to list all the properties?
    // - For the following (occupancy, propertyType): For a single property, i can easily just take the first indexed property, but for multiple properties should we list them all out in a comma separated list?  Or just leave as is and show the first property only?
    occupancy: "??", //  Ex) Non-Owner Occupied Investment
    propertyType: loanData?.subjectProperties[0]?.propertyType,
    subjectProperties: loanData?.subjectProperties, // For displaying multiple properties

    // ------------------------------------------------------------------

    // ##### For displaying of the Guarantors #####
    // To display the guarantors on this pdf, should these be mapped from loanData.borrowers where isGuarantor on the borrower level is true?
    guarantors: loanData?.borrowers?.filter((b) => b.isGuarantor),

    // ------------------------------------------------------------------

    // ##### For displaying of the Loan Term information #####
    // Are the following pulling from the correct areas?
    // - From Loan Data
    loanProductType: loanData?.loanProductType,
    exitStrategy: loanData?.exitStrategy,
    loanTerm: loanData?.termMonths,
    interestRate: loanData?.interestRate,
    interestType: loanData?.interestType, // Ex) interestType: "Non-Dutch (as drawn)",
    amortization: loanData?.amortization,
    totalLoanAmount: loanData?.totalLoanAmount,
    initialLoanAmount: loanData?.baseLoanAmount,
    purchasePrice: loanData?.totalPurchasePrice, // property level or loan level
    loanToCost: loanData?.baseLoanToCost, // Ex)  loanToCost: "80.00%",
    loanToValue: loanData?.totalLoanToArv, // Ex) loanToValue: "50.00%",
    arvLtv: loanData?.totalLoanToArv, // Ex) arvLtv: "70.00%",
    asIsValue: loanData?.totalAsIsValue,
    estimatedArv: loanData?.totalArv,
    constructionHoldback: loanData?.totalHoldback,
    constructionBudget: loanData?.totalConstructionBudget,

    // - From Loan Terms
    entityName: loanTerms?.entityName,
    originationFee: loanTerms?.totalOriginatorFees, // Percent or dollar? Currently is a dollar value in loan data. In mockup it's a percent. // Ex) originationFee:  "7.99%",

    // ##### Questions about non-existant fields #####
    // - Questions regarding the specific fields are noted next to each field
    // - As of now, these are editable in the form, but not currently found on the loan data anywhere
    //   Ex) denotes "Example value listed in the provided mockup"
    lienPosition: "2nd Position", // Does this exist anywhere? If not, where are we adding, and what data type is it, and what are the selection of options if any? // Ex) amortization: "Interest Only",
    legalFee: 0, // Is this a sum of loanTerms?.thirdPartyFees where feeCategory === 'Underwriting Fee'? // Ex) legalFee: "$77,774.00",
    underwritingFee: 0, // Is this a sum of loanTerms?.thirdPartyFees where feeCategory === 'Underwriting Fee'?  // Ex)  underwritingFee: "$79,000.00",
    paymentReserve: "N/A", // Does this exist anywhere? If not where are we adding, and what data type is it? // Ex)  paymentReserve: "N/A",
  };
};

const LoanFeesSubTabLetterOfIntent = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const [showPreview, setShowPreview] = useState(false);

  const formInitialValues = useMemo(() => {
    return mapLoanDataToLetterOfIntent({ loanData: loanDrilldown, loanTerms });
  }, [loanDrilldown, loanTerms]);

  return (
    <FormWrapper
      // formSpy
      initialValues={formInitialValues}
    >
      <PrimaryContentCard
        maxWidth="md"
        loading={isNil(loanTerms)}
        title="Letter of Intent"
        endContent={
          <Flx ac gap={1}>
            <Button
              variant="outlined"
              startIcon={
                showPreview ? (
                  <VisibilityOffOutlined className="thin" />
                ) : (
                  <VisibilityOutlined className="thin" />
                )
              }
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? "Hide Preview" : "Show Preview"}
            </Button>
            <DownloadLetterOfIntentButton />
          </Flx>
        }
      >
        {showPreview ? (
          <LetterOfIntentPreview />
        ) : (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 2 }} />
            <Flx column g={2}>
              <Flx g={1}>
                <Txt fontSize={16} bold>
                  Entity Name:
                </Txt>
                <Txt fontSize={16}>{loanTerms?.entityName}</Txt>
              </Flx>
              <Flx g={1}>
                <Txt fontSize={16} bold>
                  Loan Product:
                </Txt>
                <Txt fontSize={16}>{loanDrilldown?.loanProductType}</Txt>
              </Flx>

              <Flx column g={0.5}>
                {loanDrilldown?.subjectProperties?.map((p) => {
                  return (
                    <Flx g={1}>
                      <Txt fontSize={16} bold>
                        Property Address:
                      </Txt>
                      <Txt fontSize={16} key={p._id}>
                        {formatAddressObjectFullAddress(p.address)}
                      </Txt>
                    </Flx>
                  );
                })}
              </Flx>
              <RffRenderedGuarantors />
            </Flx>
            <Divider sx={{ my: 2 }} />
            <LetterOfIntentForm />
          </Box>
        )}
      </PrimaryContentCard>
    </FormWrapper>
  );
};

const RffRenderedGuarantors = ({ children }) => {
  const { values } = useFormState();
  return (
    <Flx g={1}>
      <Txt fontSize={16} bold>
        Guarantors:
      </Txt>
      <Flx column g={0.5}>
        {values?.guarantors?.map((p) => {
          return (
            <Txt fontSize={16} key={p._id}>
              {formatBorrowerObjectFullName(p)}
            </Txt>
          );
        })}
      </Flx>
    </Flx>
  );
};

const FormWrapper = ({ initialValues, children, formSpy }) => {
  return (
    <RffForm suppressFooter formSpy={formSpy} initialValues={initialValues}>
      {children}
    </RffForm>
  );
};

const LetterOfIntentPreview = ({ isHHb = false }) => {
  const { values } = useFormState();
  return (
    <PdfPreviewer sx={{ height: "78vh", width: "100%" }}>
      {isHHb ? (
        <LoanTermsSheetHhbPDF formData={values} />
      ) : (
        <LoanTermsSheetPDF formData={values} />
      )}
    </PdfPreviewer>
  );
};

const LetterOfIntentForm = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);

  return <LetterOfIntentFormQuestions />;
};

const LetterOfIntentFormQuestions = ({ children }) => {
  const letColumnFields = useMemo(
    () => [
      // Left Column
      {
        name: "lienPosition",
        label: "Lien",
        type: "text",
      },
      {
        name: "propertyType",
        label: "Property Type",
        type: "text",
      },
      {
        name: "purchasePrice",
        label: "Purchase Price",
        type: "dollar",
      },
      {
        name: "constructionBudget",
        label: "Construction Budget",
        type: "dollar",
      },
      {
        name: "asIsValue",
        label: "Estimated As-Is Value",
        type: "dollar",
      },
      {
        name: "estimatedArv",
        label: "Estimated ARV",
        type: "dollar",
      },
      {
        name: "occupancy",
        label: "Occupancy",
        type: "text",
      },
      {
        name: "exitStrategy",
        label: "Exit Strategy",
        type: "text",
      },
      {
        name: "totalLoanAmount",
        label: "Total Loan Amount",
        type: "dollar",
      },
      {
        name: "initialLoanAmount",
        label: "Initial Loan Amount",
        type: "dollar",
      },
      {
        name: "constructionHoldback",
        label: "Construction Holdback",
        type: "dollar",
      },
    ],
    [],
  );
  const rightColumnFields = useMemo(
    () => [
      // Right Column
      {
        name: "loanTerm",
        label: "Loan Term",
        type: "text",
      },
      {
        name: "interestRate",
        label: "Interest Rate",
        type: "percent",
      },
      {
        name: "interestType",
        label: "Interest Type",
        type: "text",
      },
      {
        name: "amortization",
        label: "Amortization",
        type: "text",
      },
      {
        name: "originationFee",
        label: "Origination Fee",
        type: "dollar",
      },
      {
        name: "legalFee",
        label: "Legal Fee",
        type: "dollar",
      },
      {
        name: "underwritingFee",
        label: "Underwriting Fee",
        type: "dollar",
      },
      {
        name: "paymentReserve",
        label: "Payment Reserve",
        type: "text",
      },
      {
        name: "loanToCost",
        label: "Loan to Cost",
        type: "percent",
      },
      {
        name: "loanToValue",
        label: "Loan to Value",
        type: "percent",
      },
      {
        name: "arvLtv",
        label: "Estimated ARV",
        type: "percent",
      },
    ],
    [],
  );
  return (
    <FlxGrid gap={4}>
      <RffGroup>
        <RffFieldsRenderer fields={letColumnFields} />
      </RffGroup>
      <RffGroup>
        <RffFieldsRenderer fields={rightColumnFields} />
      </RffGroup>
    </FlxGrid>
  );
};

const DownloadLetterOfIntentButton = memo(({}) => {
  const { values } = useFormState();
  // const values = {};

  const { downloadPDF } = usePdfDownloadHook({
    formData: values,
    filename: "letter-of-intent-WIP.pdf",
    type: "loan-terms-sheet",
  });

  return (
    <Button
      sx={{ flexGrow: 1 }}
      size="small"
      variant="contained"
      startIcon={<FileDownloadOutlined className="thin" />}
      onClick={downloadPDF}
    >
      Download (PDF)
    </Button>
  );
});

const DUMMY = {
  lienPosition: "2nd Position",
  propertyType: "Ground Up Construction",
  purchasePrice: "$4,350,000.00",
  constructionBudget: "$28,000,000.00",
  asIsValue: "$4,350,000.00",
  estimatedArv: "$38,940,000.00",
  occupancy: "Non-Owner Occupied Investment",
  exitStrategy: "Resale",
  totalLoanAmount: "$30,000,000.00",
  initialLoanAmount: "$2,000,000.00",
  constructionHoldback: "$28,000,000.00",
  loanTerm: "24 months",
  interestRate: "8.99%",
  interestType: "Non-Dutch (as drawn)",
  amortization: "Interest Only",
  originationFee: "7.99%",
  legalFee: "$77,774.00",
  underwritingFee: "$79,000.00",
  paymentReserve: "N/A",
  loanToCost: "80.00%",
  loanToValue: "50.00%",
  arvLtv: "70.00%",
};

export default LoanFeesSubTabLetterOfIntent;
